/**
 * Explorer Timeline Editor
 *
 * Admin interface for managing Explorer → Explore Connections timeline events
 * Integrates with global Update button - no local save button
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Plus, Upload, X, GripVertical, MoreVertical, ChevronUp, ChevronDown } from 'lucide-react';
import {
  fetchTimelineEvents,
  saveAllTimelineEvents,
  uploadTimelineImage,
  getTimelineImageUrl,
} from '../lib/explorerTimelineService';

interface ExplorerTimelineEditorProps {
  songId: string;
  onContentChange?: (hasChanges: boolean) => void;
  modalContext?: 'look_deeper' | 'explore_connections';
}

export interface ExplorerTimelineEditorHandle {
  save: () => Promise<boolean>;
  hasUnsavedChanges: () => boolean;
}

interface TimelineEventEdit {
  id?: string;
  sort_order: number;
  year: string;
  title: string;
  description: string;
  image_path: string | null;
  detail_image_path: string | null;
  _tempId?: string;
}

const ExplorerTimelineEditor = forwardRef<ExplorerTimelineEditorHandle, ExplorerTimelineEditorProps>(
  ({ songId, onContentChange, modalContext = 'explore_connections' }, ref) => {
  const [events, setEvents] = useState<TimelineEventEdit[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [originalEvents, setOriginalEvents] = useState<string>('');
  const lastEventRef = useRef<HTMLDivElement>(null);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [draggedEventIndex, setDraggedEventIndex] = useState<number | null>(null);
  const [activeKebabMenu, setActiveKebabMenu] = useState<number | null>(null);

  useEffect(() => {
    loadEvents();
  }, [songId]);

  useEffect(() => {
    const currentSnapshot = JSON.stringify(events);
    const isDirty = currentSnapshot !== originalEvents;
    onContentChange?.(isDirty);
  }, [events, originalEvents, onContentChange]);

  async function loadEvents() {
    setLoading(true);
    try {
      const data = await fetchTimelineEvents(songId);
      const editData: TimelineEventEdit[] = data.map((e) => ({
        id: e.id,
        sort_order: e.sort_order,
        year: e.year,
        title: e.title,
        description: e.description,
        image_path: e.image_path,
        detail_image_path: e.detail_image_path,
      }));
      setEvents(editData);
      setOriginalEvents(JSON.stringify(editData));
    } catch (err) {
      console.error('[ExplorerTimelineEditor] Error loading events:', err);
      setEvents([]);
      setOriginalEvents('[]');
    } finally {
      setLoading(false);
    }
  }

  function addEvent() {
    const newEvent: TimelineEventEdit = {
      _tempId: `temp-${Date.now()}`,
      sort_order: events.length + 1,
      year: String(new Date().getFullYear()),
      title: '',
      description: '',
      image_path: null,
      detail_image_path: null,
    };
    const newEvents = [...events, newEvent];
    setEvents(newEvents);

    // Scroll to new event after render
    setTimeout(() => {
      lastEventRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const yearInput = document.getElementById(`year-input-${events.length}`);
      yearInput?.focus();
    }, 100);
  }

  function removeEvent(index: number) {
    const updated = events.filter((_, i) => i !== index);
    updated.forEach((event, i) => {
      event.sort_order = i + 1;
    });
    setEvents(updated);
    // Clear validation error for this index
    const newErrors = { ...validationErrors };
    delete newErrors[index];
    setValidationErrors(newErrors);
  }

  function updateEvent(index: number, updates: Partial<TimelineEventEdit>) {
    const updated = [...events];
    updated[index] = { ...updated[index], ...updates };
    setEvents(updated);

    // Clear validation error if field is now valid
    if (updates.year || updates.title) {
      const newErrors = { ...validationErrors };
      delete newErrors[index];
      setValidationErrors(newErrors);
    }
  }

  function moveEventUp(index: number) {
    if (index === 0) return;
    const updated = [...events];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((event, i) => {
      event.sort_order = i + 1;
    });
    setEvents(updated);
    setActiveKebabMenu(null);
  }

  function moveEventDown(index: number) {
    if (index === events.length - 1) return;
    const updated = [...events];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((event, i) => {
      event.sort_order = i + 1;
    });
    setEvents(updated);
    setActiveKebabMenu(null);
  }

  function handleDragStart(index: number) {
    setDraggedEventIndex(index);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    if (draggedEventIndex === null || draggedEventIndex === index) return;

    const updated = [...events];
    const [movedEvent] = updated.splice(draggedEventIndex, 1);
    updated.splice(index, 0, movedEvent);
    updated.forEach((event, i) => {
      event.sort_order = i + 1;
    });
    setEvents(updated);
    setDraggedEventIndex(index);
  }

  function handleDragEnd() {
    setDraggedEventIndex(null);
  }

  async function handleImageUpload(index: number, file: File) {
    setUploadingIndex(index);
    try {
      const result = await uploadTimelineImage(file, songId);
      if (result.success && result.path) {
        updateEvent(index, { image_path: result.path });
      } else {
        console.error('[ExplorerTimelineEditor] Upload failed:', result.error);
      }
    } catch (err) {
      console.error('[ExplorerTimelineEditor] Upload error:', err);
    } finally {
      setUploadingIndex(null);
    }
  }

  async function saveEvents(): Promise<boolean> {
    // Validate required fields
    const errors: Record<number, string> = {};
    events.forEach((event, index) => {
      if (!event.year.trim() || !event.title.trim()) {
        errors[index] = 'Year and Title are required';
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return false;
    }

    try {
      const result = await saveAllTimelineEvents(songId, events);
      if (result.success) {
        await loadEvents();
        setValidationErrors({});
        return true;
      } else {
        console.error('[ExplorerTimelineEditor] Save failed:', result.error);
        return false;
      }
    } catch (err) {
      console.error('[ExplorerTimelineEditor] Save error:', err);
      return false;
    }
  }

  // Expose save and dirty check to parent
  useImperativeHandle(ref, () => ({
    save: saveEvents,
    hasUnsavedChanges: () => {
      const currentSnapshot = JSON.stringify(events);
      return currentSnapshot !== originalEvents;
    },
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-[#40E0D0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        {modalContext === 'look_deeper' && (
          <p className="text-[12px] text-[#808080]">
            Add chronological events for historical context
          </p>
        )}
        <button
          onClick={addEvent}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#282828] hover:bg-[#333333] rounded text-[12px] text-[#F5F5F5] font-medium transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Event
        </button>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-8 text-[#606060] text-[13px] bg-[#0A0A0A] border border-[#282828] rounded-lg">
          No timeline events yet.
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event, index) => {
            const imageUrl = event.image_path ? getTimelineImageUrl(event.image_path) : null;
            const isLastEvent = index === events.length - 1;
            const hasError = validationErrors[index];

            return (
              <div
                key={event._tempId || event.id || index}
                ref={isLastEvent ? lastEventRef : null}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`bg-[#0A0A0A] border border-[#282828] rounded-lg p-4 transition-opacity cursor-move ${
                  draggedEventIndex === index ? 'opacity-50' : 'opacity-100'
                }`}
              >
                {/* Header with controls */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-[#606060] cursor-grab active:cursor-grabbing" />
                    <span className="text-[12px] font-medium text-[#808080]">
                      Event {index + 1}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    {events.length > 1 && (
                      <div className="relative">
                        <button
                          onClick={() => setActiveKebabMenu(activeKebabMenu === index ? null : index)}
                          className="p-1 hover:bg-[#282828] rounded transition-colors"
                          aria-label="Reorder event"
                        >
                          <MoreVertical className="w-4 h-4 text-[#808080]" />
                        </button>

                        {activeKebabMenu === index && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setActiveKebabMenu(null)}
                            />
                            <div className="absolute right-0 top-full mt-1 bg-[#1A1A1A] border border-[#282828] rounded-lg shadow-xl py-1 min-w-[140px] z-20">
                              <button
                                onClick={() => moveEventUp(index)}
                                disabled={index === 0}
                                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#F5F5F5] hover:bg-[#282828] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronUp className="w-3.5 h-3.5" />
                                Move Up
                              </button>
                              <button
                                onClick={() => moveEventDown(index)}
                                disabled={index === events.length - 1}
                                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-[#F5F5F5] hover:bg-[#282828] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronDown className="w-3.5 h-3.5" />
                                Move Down
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => removeEvent(index)}
                      className="p-1 hover:bg-[#282828] rounded transition-colors"
                      aria-label="Delete event"
                    >
                      <X className="w-4 h-4 text-[#808080]" />
                    </button>
                  </div>
                </div>

                {/* Validation error */}
                {hasError && (
                  <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-[11px] text-red-400">{hasError}</p>
                  </div>
                )}

                {/* Grid layout matching Context in Time */}
                <div className="grid grid-cols-[auto_1fr] gap-3">
                  {/* Year */}
                  <div>
                    <label className="block text-[11px] font-medium text-[#A0A0A0] mb-1.5">
                      Year
                    </label>
                    <input
                      id={`year-input-${index}`}
                      type="text"
                      value={event.year}
                      onChange={(e) => updateEvent(index, { year: e.target.value })}
                      placeholder="e.g. 2024, 1999-2005, Today"
                      className="w-36 px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-[11px] font-medium text-[#A0A0A0] mb-1.5">
                      Title
                    </label>
                    <input
                      type="text"
                      value={event.title}
                      onChange={(e) => updateEvent(index, { title: e.target.value })}
                      placeholder="Event title or headline"
                      className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Description */}
                  <div className="col-span-2">
                    <label className="block text-[11px] font-medium text-[#A0A0A0] mb-1.5">
                      Description
                    </label>
                    <textarea
                      value={event.description}
                      onChange={(e) => updateEvent(index, { description: e.target.value })}
                      placeholder="Brief context or description"
                      rows={2}
                      className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Timeline Image Upload - Full Width */}
                  <div className="col-span-2">
                    <label className="block text-[11px] font-medium text-[#A0A0A0] mb-1.5">
                      Timeline Image
                    </label>

                    {imageUrl ? (
                      <div className="relative">
                        {/* Preview area with circular thumbnail */}
                        <div className="flex items-center gap-3 p-3 bg-[#181818] border border-[#282828] rounded-lg">
                          {/* Circular preview */}
                          <div
                            className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/20 bg-[#0A0A0A] flex-shrink-0"
                            style={{
                              backgroundImage: `url(${imageUrl})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }}
                          />

                          {/* Image info and actions */}
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] text-[#F5F5F5] mb-1">Image uploaded</p>
                            <div className="flex items-center gap-2">
                              <label
                                htmlFor={`image-upload-${index}`}
                                className="text-[11px] text-[#40E0D0] hover:underline cursor-pointer"
                              >
                                Replace
                              </label>
                              <span className="text-[#606060]">•</span>
                              <button
                                onClick={() => updateEvent(index, { image_path: null })}
                                className="text-[11px] text-red-400 hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Hidden file input */}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(index, file);
                          }}
                          disabled={uploadingIndex === index}
                          className="hidden"
                          id={`image-upload-${index}`}
                        />
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(index, file);
                          }}
                          disabled={uploadingIndex === index}
                          className="hidden"
                          id={`image-upload-${index}`}
                        />
                        <label
                          htmlFor={`image-upload-${index}`}
                          className="flex items-center justify-center gap-2 w-full h-24 border-2 border-dashed border-[#282828] rounded-lg cursor-pointer hover:border-[#40E0D0]/50 hover:bg-[#40E0D0]/5 transition-colors"
                        >
                          {uploadingIndex === index ? (
                            <div className="w-5 h-5 border-2 border-[#40E0D0] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-4 h-4 text-[#808080]" />
                              <span className="text-[12px] text-[#808080]">
                                Click to upload timeline image
                              </span>
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

ExplorerTimelineEditor.displayName = 'ExplorerTimelineEditor';

export default ExplorerTimelineEditor;
